/**
 * The "Graph" member control on every us.elect.info page (sites#355).
 *
 * Entity pages load entity-graph.js, which registers the real control (the
 * graph pop-over): candidates, committees and geographies from us-pages, and
 * the leaf pages once their detail has rendered (graph-target.ts, which
 * replaces this placeholder: the bar keeps one control per id). Everywhere else this registers the same
 * control so the member tools look the same site-wide: locked for anonymous
 * visitors like every member control, and for members a short hint pointing
 * at the pages that have a graph.
 */

export const GRAPH_ICON =
  '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5">' +
  '<circle cx="8" cy="8" r="2.2"/><circle cx="2.8" cy="3.2" r="1.6"/><circle cx="13.2" cy="3.6" r="1.6"/>' +
  '<circle cx="3.2" cy="12.8" r="1.6"/><circle cx="13" cy="12.6" r="1.6"/>' +
  '<path d="M6.3 6.6 4 4.3M9.8 6.8l2.2-2.2M6.4 9.5l-2 2M9.7 9.4l2.1 2"/></svg>';

export const GRAPH_HINT = 'Open a candidate, committee, donor, voter or geography page to see its connections as a graph.';

const STYLE = `
.ei-graph-hint{position:absolute;z-index:1000;max-width:260px;margin:0;padding:8px 12px;background:#fff;border:1px solid #e2e8f0;
  border-radius:6px;box-shadow:0 8px 24px rgba(26,54,93,.16);color:#2d3748;font-size:.85rem;line-height:1.4}`;

function showHint(button: HTMLElement): void {
  const doc = button.ownerDocument;
  doc.getElementById('ei-graph-hint')?.remove();
  if (!doc.getElementById('ei-graph-hint-css')) {
    const style = doc.createElement('style');
    style.id = 'ei-graph-hint-css';
    style.textContent = STYLE;
    doc.head.appendChild(style);
  }
  const hint = doc.createElement('p');
  hint.id = 'ei-graph-hint';
  hint.className = 'ei-graph-hint';
  hint.setAttribute('role', 'status');
  hint.textContent = GRAPH_HINT;
  doc.body.appendChild(hint);
  const r = button.getBoundingClientRect();
  hint.style.top = `${r.bottom + window.scrollY + 6}px`;
  const left = Math.min(r.left + window.scrollX, window.scrollX + doc.documentElement.clientWidth - hint.offsetWidth - 8);
  hint.style.left = `${Math.max(8, left)}px`;
  const close = (): void => {
    hint.remove();
    doc.removeEventListener('mousedown', onDoc, true);
    doc.removeEventListener('keydown', onKey, true);
  };
  const onDoc = (e: Event): void => {
    if (e.target !== button && !button.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  doc.addEventListener('mousedown', onDoc, true);
  doc.addEventListener('keydown', onKey, true);
  window.setTimeout(close, 6000);
}

type ControlHost = { electinfoBarControls?: { push: (...c: object[]) => void } | object[] };

/** Register the placeholder Graph control on pages without the graph pop-over. */
export function initGraphPlaceholder(): void {
  const register = (): void => {
    // Every script tag is parsed by now: entity-graph.js present means the real control.
    if (document.querySelector('script[src*="/js/entity-graph.js"]')) return;
    const w = window as unknown as ControlHost;
    const queue = (w.electinfoBarControls = w.electinfoBarControls || []) as { push: (...c: object[]) => void };
    queue.push({
      id: 'graph',
      label: 'Graph',
      requires: 'graph',
      title: 'Connections as a graph, on entity pages',
      icon: GRAPH_ICON,
      onActivate: (ctx: { button: HTMLElement }) => showHint(ctx.button),
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', register, { once: true });
  else register();
}
